import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Client-side wishlist / favorites for the public storefront (guest-first — no
 * account needed). Scoped to ONE store slug; visiting a different store starts
 * a fresh list. Stores item ids only; the saved page re-fetches each product.
 */
interface WishlistState {
  slug: string | null;
  items: string[];
  toggle(slug: string, itemId: string): void;
  remove(itemId: string): void;
  clear(): void;
}

export const useWishlistStore = create<WishlistState>()(
  persist(
    (set) => ({
      slug: null,
      items: [],
      toggle: (slug, itemId) =>
        set((state) => {
          const items = state.slug === slug ? [...state.items] : [];
          const idx = items.indexOf(itemId);
          if (idx >= 0) items.splice(idx, 1);
          else items.unshift(itemId);
          return { slug, items };
        }),
      remove: (itemId) => set((state) => ({ items: state.items.filter((x) => x !== itemId) })),
      clear: () => set({ items: [] }),
    }),
    { name: 'erp-store-wishlist', storage: createJSONStorage(() => localStorage) },
  ),
);

/** Count of saved items for the active store (0 when viewing a different store). */
export const wishlistCount = (slug: string, state: { slug: string | null; items: string[] }): number =>
  state.slug === slug ? state.items.length : 0;
